import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { getProfileDefaultTimeZone } from '@/helpers/marketplaceUnion.js';

// Подключаем плагины dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

// Caller options are merged over these defaults (not a replacement). To drop a default,
// pass the key explicitly as undefined (see DATE_ONLY_FORMAT_OPTIONS).
const DB_DATE_TIME_DEFAULTS = {
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
};

// Date part only: unsets the time defaults of formatDBDateTimeWithTimeZone.
export const DATE_ONLY_FORMAT_OPTIONS = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: undefined,
  minute: undefined,
  hour12: undefined,
  timeZoneName: undefined,
};

// Default look of formatInstantInZone: "Oct 23, 2026, 9:14 AM PDT" in an English browser.
const INSTANT_DEFAULTS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

// Keys that make an output a date-time; dropped for date-only output.
const TIME_KEYS = ['hour', 'minute', 'second', 'fractionalSecondDigits', 'hour12', 'hourCycle', 'timeZoneName'];

// Intl.DateTimeFormat construction is expensive and grids format one value per cell, so
// instances are cached by locale + options. `undefined` options are dropped from the key
// (Intl treats them as absent).
const formatterCache = new Map();
const getIntlFormatter = (locale, options) => {
  const entries = Object.entries(options)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  const key = `${locale || ''}|${JSON.stringify(entries)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, Object.fromEntries(entries));
    formatterCache.set(key, formatter);
  }
  return formatter;
};

const validZoneCache = new Map();
const isValidTimeZone = (zone) => {
  if (!zone || typeof zone !== 'string') return false;
  if (!validZoneCache.has(zone)) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: zone });
      validZoneCache.set(zone, true);
    } catch {
      validZoneCache.set(zone, false);
    }
  }
  return validZoneCache.get(zone);
};

const DAY_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PART = '(\\d{2}:\\d{2}(?::\\d{2}(?:[.,]\\d+)?)?)';
const NAIVE_DATE_TIME_RE = new RegExp(`^(\\d{4}-\\d{2}-\\d{2})[T ]${TIME_PART}$`);
const OFFSET_DATE_TIME_RE = new RegExp(
  `^(\\d{4}-\\d{2}-\\d{2})[T ]${TIME_PART}\\s*(Z|[+-]\\d{2}(?::?\\d{2})?)$`,
  'i',
);

const isCalendarDayString = (value) => typeof value === 'string' && DAY_ONLY_RE.test(value.trim());

/**
 * Parses a backend timestamp into a real instant. A string with no offset ("2026-10-23 14:05:09",
 * as ClickHouse returns it) is UTC, never browser-local, which is what `new Date(str)` and
 * `dayjs(str)` would assume. Accepts ISO strings with `Z` or an offset, Date, epoch millis and
 * dayjs objects. A bare `YYYY-MM-DD` parses as UTC midnight.
 * @param {string|number|Date|object|null|undefined} value
 * @returns {Date|null} null for empty or unparseable input
 */
export const parseInstant = (value) => {
  if (value === null || value === undefined || value === '') return null;
  let date = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else if (dayjs.isDayjs(value)) {
    date = value.toDate();
  } else if (typeof value === 'string') {
    const text = value.trim();
    const naive = NAIVE_DATE_TIME_RE.exec(text);
    const offset = OFFSET_DATE_TIME_RE.exec(text);
    if (DAY_ONLY_RE.test(text)) {
      date = new Date(`${text}T00:00:00Z`);
    } else if (naive) {
      date = new Date(`${naive[1]}T${naive[2].replace(',', '.')}Z`);
    } else if (offset) {
      let zone = offset[3].toUpperCase();
      if (zone !== 'Z') {
        const digits = zone.slice(1).replace(':', '');
        zone = `${zone[0]}${digits.slice(0, 2)}:${digits.slice(2) || '00'}`;
      }
      date = new Date(`${offset[1]}T${offset[2].replace(',', '.')}${zone}`);
    } else {
      date = new Date(text);
    }
  }
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

/**
 * Short zone name for a zone at an instant ("PDT" in summer, "PST" in winter), `en-US` names for
 * stable codes. Falls back to a GMT offset where Intl has no abbreviation, and to the zone id
 * itself when the zone is unknown. Same label as the header marketplace clock.
 * @param {string} timeZone IANA zone
 * @param {string|number|Date} [instant] defaults to now
 * @returns {string}
 */
export const zoneAbbrev = (timeZone, instant = new Date()) => {
  if (!isValidTimeZone(timeZone)) return timeZone || '';
  const date = parseInstant(instant) || new Date();
  const part = getIntlFormatter('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName');
  return part?.value || timeZone;
};

/**
 * Formats a real instant in an IANA zone, with a short zone suffix: "Oct 23, 2026, 9:14 AM PDT".
 * Options are `Intl.DateTimeFormat` options merged over that default (pass a key as undefined to
 * drop it), plus `locale` (default: the browser's), `fallback` (default ''), returned for empty
 * or invalid input, and `dateOnly`, which drops the time and the suffix. An unknown zone formats
 * in UTC. A bare `YYYY-MM-DD` string is a calendar day, not an instant: it is never shifted and
 * never gets a time or suffix.
 * @param {string|number|Date} value
 * @param {string} [timeZone='UTC']
 * @param {object} [options]
 * @returns {string}
 */
export const formatInstantInZone = (value, timeZone = 'UTC', options = {}) => {
  const { locale, fallback = '', dateOnly = false, ...intlOptions } = options;
  const date = parseInstant(value);
  if (!date) return fallback;
  const calendarDay = isCalendarDayString(value);
  const merged = {
    ...INSTANT_DEFAULTS,
    ...intlOptions,
    timeZone: calendarDay || !isValidTimeZone(timeZone) ? 'UTC' : timeZone,
  };
  if (dateOnly || calendarDay) {
    TIME_KEYS.forEach((key) => {
      merged[key] = undefined;
    });
  }
  return getIntlFormatter(locale, merged).format(date);
};

/**
 * Legacy-look date-time ("10/23/2026, 09:14 PDT", 24 h) in an IANA zone; caller options are
 * merged over DB_DATE_TIME_DEFAULTS. Same parsing, zone handling and caching as
 * formatInstantInZone (an unparseable value formats as '').
 */
export const formatDBDateTimeWithTimeZone = (dbDate, timezone = 'UTC', formatOptions = {}) =>
  formatInstantInZone(dbDate, timezone, { ...DB_DATE_TIME_DEFAULTS, ...formatOptions });

/**
 * The zone a grid cell shows a date-time in. The one place that decides; callers only pass what
 * they have.
 *  1. A column pinned with `display_zone` (a valid IANA zone, in practice 'UTC' for the columns
 *     whose header says "(UTC)") always renders there, on every page.
 *  2. A channel page (eBay, Shopify, Walmart, Meta: `context.channelMode`) never uses the Amazon
 *     marketplace or the profile's Amazon default: it uses its own channel zone
 *     (`context.channelTimeZone`, the selected account's), else UTC.
 *  3. An inventory page (`context.inventoryMode`) hides the page marketplace, so it skips it:
 *     the business profile's default marketplace zone, then UTC.
 *  4. An Amazon page: the page marketplace (`context.marketplace.timeZone`), then the profile's
 *     default marketplace zone (seller-only marketplaces carry `timeZone: null`), then UTC.
 * Never the browser zone.
 * @param {{ marketplace?: object|null, businessProfile?: object|null, channelMode?: boolean,
 *   inventoryMode?: boolean, channelTimeZone?: string|null }|null} [context]
 *   the OLAP grid context (`params.context`) or `{ marketplace, businessProfile }` from the
 *   account store
 * @param {object|null} [colDef] the AG Grid column def (`params.colDef`); its `display_zone` is
 *   read from `colDef.context.config`
 * @returns {string} IANA zone
 */
export const resolveMarketplaceTimeZone = (context, colDef) => {
  const pinned = colDef?.context?.config?.display_zone;
  if (isValidTimeZone(pinned)) return pinned;
  if (context?.channelMode) {
    return isValidTimeZone(context.channelTimeZone) ? context.channelTimeZone : 'UTC';
  }
  const own = context?.inventoryMode ? null : context?.marketplace?.timeZone;
  if (isValidTimeZone(own)) return own;
  const profileZone = getProfileDefaultTimeZone(context?.businessProfile);
  return isValidTimeZone(profileZone) ? profileZone : 'UTC';
};

/**
 * AG Grid valueFormatter for the audit date columns (audit_last_updated_datetime /
 * audit_last_updated_date): marketplace-timezone date-time or date only. The zone follows
 * resolveMarketplaceTimeZone, so a grid context without a marketplace still formats. Returns
 * the raw value for an unparseable one.
 * @param {boolean} includeTime
 */
export const buildAuditDateFormatter = (includeTime) => (params) => {
  if (!params.value) return '';
  const tz = resolveMarketplaceTimeZone(params.context, params.colDef);
  const options = includeTime ? {} : DATE_ONLY_FORMAT_OPTIONS;
  return formatDBDateTimeWithTimeZone(params.value, tz, { ...options, fallback: params.value });
};

// 'YYYY-MM-DD' of a value in a zone (browser zone when null). A calendar-day string is that
// day already and is never shifted.
const dayInZone = (date, timezone) => {
  if (isCalendarDayString(date)) return date.trim();
  const parsed = dayjs(parseInstant(date));
  return (timezone ? parsed.tz(timezone) : parsed).format('YYYY-MM-DD');
};

export const isToday = (date, timezone = null) => {
  if (!date) {
    return false;
  }

  try {
    const today = timezone ? dayjs().tz(timezone) : dayjs();

    return dayInZone(date, timezone) === today.format('YYYY-MM-DD');
  } catch (error) {
    console.error('Error checking if date is today:', error);
    return false;
  }
};

export const isYesterday = (date, timezone = null) => {
  if (!date) {
    return false;
  }

  try {
    const yesterday = timezone
      ? dayjs().tz(timezone).subtract(1, 'day')
      : dayjs().subtract(1, 'day');

    return dayInZone(date, timezone) === yesterday.format('YYYY-MM-DD');
  } catch (error) {
    console.error('Error checking if date is yesterday:', error);
    return false;
  }
};

/**
 * Short calendar date for connection/authorization cards: "Jun 24, 2026".
 * Native-Date based (no timezone) — returns `fallback` for empty/invalid input.
 * @param {string|Date} dateString
 * @param {string} [fallback='-']
 * @returns {string}
 */
export const formatShortDate = (dateString, fallback = '-') => {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Bucketed relative time for connection/authorization cards:
 * Today / Yesterday / "N days ago" / "N weeks ago" / "N months ago", falling back
 * to formatShortDate for >1y or future dates.
 * @param {string|Date} dateString
 * @returns {string}
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const diffDays = Math.floor((new Date() - date) / 86400000);
  if (diffDays < 0) return formatShortDate(dateString);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const w = Math.floor(diffDays / 7);
    return w === 1 ? '1 week ago' : `${w} weeks ago`;
  }
  if (diffDays < 365) {
    const m = Math.floor(diffDays / 30);
    return m === 1 ? '1 month ago' : `${m} months ago`;
  }
  return formatShortDate(dateString);
};

/**
 * Day.js date with a caller-chosen pattern in the browser's local zone ("Jun 24, 2026").
 * Returns `fallback` for empty/invalid input.
 * @param {string|Date} value
 * @param {string} [format='MMM D, YYYY']
 * @param {string} [fallback='—']
 * @returns {string}
 */
export const formatDateWithPattern = (value, format = 'MMM D, YYYY', fallback = '—') => {
  const d = value ? dayjs(value) : null;
  return d?.isValid() ? d.format(format) : fallback;
};

/**
 * Browser-locale date-time (`Date#toLocaleString`, local zone) used by the AI-connection and
 * webhook grids and the connection detail cards. Returns `fallback` for empty input. `locale`
 * and `options` are passed to `toLocaleString` as they are (default: the browser's locale and
 * format).
 * @param {string|Date} value
 * @param {string} [fallback='—']
 * @param {string|string[]} [locale]
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export const formatLocaleDateTime = (value, fallback = '—', locale, options) =>
  value ? new Date(value).toLocaleString(locale, options) : fallback;

/**
 * Browser-locale date (`Date#toLocaleDateString`, local zone). Same contract as
 * formatLocaleDateTime: `fallback` for empty input, `locale` and `options` passed through
 * unchanged (billing pages pass `'en-US'` and a month style).
 * @param {string|Date} value
 * @param {string} [fallback='—']
 * @param {string|string[]} [locale]
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export const formatLocaleDate = (value, fallback = '—', locale, options) =>
  value ? new Date(value).toLocaleDateString(locale, options) : fallback;

/**
 * Format timestamp as relative time string ("5 minutes ago")
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} - Human-readable relative time
 */
export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Never';

  try {
    const instant = parseInstant(timestamp);
    return instant ? dayjs(instant).fromNow() : 'Unknown';
  } catch (error) {
    console.error('Error formatting time ago:', error);
    return 'Unknown';
  }
};

/**
 * Получает временную зону из marketplace объекта
 * @param {Object} marketplace - Объект marketplace с timeZone
 * @param {string} fallback - Запасная временная зона (по умолчанию 'UTC')
 * @returns {string} - Временная зона
 */
export const getTimezoneFromMarketplace = (marketplace, fallback = 'UTC') => {
  return marketplace?.timeZone || fallback;
};

export const isUpdatedToday = (date, marketplace) => {
  if (!date) {
    return false;
  }

  const timezone = getTimezoneFromMarketplace(marketplace);
  return isToday(date, timezone);
};

/**
 * Returns a recency tier for an entity's last-updated timestamp:
 *   'recent' — updated within the last 24 hours (render red)
 *   'fresh'  — updated within the last 72 hours (render green)
 *   null     — older, no highlight
 *
 * Timezone-aware via the marketplace's timeZone setting.
 */
export const getUpdateRecency = (date, marketplace) => {
  if (!date) return null;
  try {
    const timezone = getTimezoneFromMarketplace(marketplace);
    const updated = dayjs(parseInstant(date)).tz(timezone);
    const now = dayjs().tz(timezone);
    const hoursAgo = now.diff(updated, 'hour', true);
    if (hoursAgo < 24) return 'recent';
    if (hoursAgo < 72) return 'fresh';
    return null;
  } catch {
    return null;
  }
};

export const formatReadableDuration = (duration) => {
  if (!duration || typeof duration !== 'string') {
    return '';
  }

  try {
    // Парсим строку и извлекаем числа
    const dayMatch = duration.match(/(\d+)\s+days?/);
    const hourMatch = duration.match(/(\d+)\s+hours?/);
    const minuteMatch = duration.match(/(\d+)\s+minutes?/);
    const secondMatch = duration.match(/(\d+)\s+seconds?/);

    const days = dayMatch ? parseInt(dayMatch[1]) : 0;
    const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
    const seconds = secondMatch ? parseInt(secondMatch[1]) : 0;

    // Создаем массив частей для отображения
    const parts = [];

    // Если есть дни - показываем только дни и часы
    if (days > 0) {
      parts.push(`${days}d`);
      if (hours > 0) {
        parts.push(`${hours}h`);
      }
    }
    // Если дней нет, но есть часы - показываем часы и минуты
    else if (hours > 0) {
      parts.push(`${hours}h`);
      if (minutes > 0) {
        parts.push(`${minutes}m`);
      }
    }
    // Если только минуты и секунды
    else {
      if (minutes > 0) {
        parts.push(`${minutes}m`);
      }
      if (seconds > 0) {
        parts.push(`${seconds}s`);
      }
    }

    // Если ничего не найдено, возвращаем оригинальную строку
    if (parts.length === 0) {
      return duration;
    }

    // Соединяем части
    return parts.join(' ');
  } catch (error) {
    console.error('Error formatting readable duration:', error);
    return duration;
  }
};

export function isDateTimeArray(value) {
  // Должен быть массив
  if (!Array.isArray(value)) {
    return false;
  }

  // Массив должен содержать от 3 до 7 элементов (минимум год, месяц, день)
  if (value.length < 3 || value.length > 7) {
    return false;
  }

  // Первый элемент (год) должен быть числом в разумном диапазоне
  if (typeof value[0] !== 'number' || value[0] < 1900 || value[0] > 2100) {
    return false;
  }

  // Второй элемент (месяц) должен быть от 1 до 12
  if (typeof value[1] !== 'number' || value[1] < 1 || value[1] > 12) {
    return false;
  }

  // Третий элемент (день) должен быть от 1 до 31
  if (typeof value[2] !== 'number' || value[2] < 1 || value[2] > 31) {
    return false;
  }

  // Все остальные элементы, если они есть, должны быть числами
  for (let i = 3; i < value.length; i++) {
    if (typeof value[i] !== 'number') {
      return false;
    }
  }

  return true;
}

export function formatDateTimeArray(value, format = 'MMM D, YYYY, HH:mm') {
  if (!isDateTimeArray(value)) {
    return value;
  }

  // Заполняем недостающие элементы массива нулями
  const fullArray = [...value];
  while (fullArray.length < 7) {
    fullArray.push(0);
  }

  // Теперь у нас есть полный массив из 7 элементов
  const [year, month, day, hour, minute, second, millisecond] = fullArray;

  // Создаем объект Date (месяцы в JavaScript начинаются с 0, но в массивах дат с 1)
  const date = new Date(year, month - 1, day, hour, minute, second, millisecond);

  // Проверяем, содержит ли время только нули (00:00:00)
  if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
    return dayjs(date).format('MM/DD/YYYY');
  }

  // Форматируем с помощью Day.js в американском формате
  return dayjs(date).format(format);
}

export function hasZeroTime(date) {
  return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
}

export function smartFormatDate(value) {
  if (!value) return value;

  let date;
  if (isDateTimeArray(value)) {
    const [year, month, day, hour, minute, second, millisecond] = value;
    date = new Date(year, month - 1, day, hour, minute, second, millisecond);
  } else {
    date = new Date(value);
  }

  // Если время 00:00:00, показываем только дату
  if (hasZeroTime(date)) {
    return dayjs(date).format('MM/DD/YYYY');
  }

  // Иначе показываем дату и время
  return dayjs(date).format('MM/DD/YYYY HH:mm:ss');
}

export function detectAndFormatDate(value) {
  // Проверяем, является ли значение массивом даты
  if (isDateTimeArray(value)) {
    return formatDateTimeArray(value);
  }

  // Проверяем, является ли значение строкой в формате ISO 8601
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return smartFormatDate(date);
    }
  }

  // Проверяем, является ли значение числом в формате timestamp (миллисекунды от начала эпохи)
  if (typeof value === 'number' && value > 1000000000000) {
    // Примерно 2001 год и позже
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return smartFormatDate(date);
    }
  }

  // Если не смогли определить формат даты, возвращаем исходное значение
  return value;
}
